import { SiteConfigurations } from '@/hooks/custom/helpcenter.hooks';
import {alpha} from "@mui/material/styles";

/**
 * 色を明るくする
 * @param color ベースカラー
 * @param amount いくつ明るくするか
 */
export const lightenColor = (color: string, amount: number) => {
  const parsedColor = parseInt(color.replace('#', ''), 16);
  const redAmount = Math.min(255, Math.max(0, Math.round(parsedColor / 0x10000 + amount)));
  const greenAmount = Math.min(
    255,
    Math.max(0, Math.round(((parsedColor / 0x100) % 0x100) + amount))
  );
  const blueAmount = Math.min(255, Math.max(0, Math.round((parsedColor % 0x100) + amount)));
  const r = redAmount < 16 ? `0${redAmount.toString(16)}` : `${redAmount.toString(16)}`;
  const g = greenAmount < 16 ? `0${greenAmount.toString(16)}` : `${greenAmount.toString(16)}`;
  const b = blueAmount < 16 ? `0${blueAmount.toString(16)}` : `${blueAmount.toString(16)}`;
  return `#${r}${g}${b}`;
};
/**
 * 色を暗くする
 * @param color ベースカラー
 * @param amount いくつ暗くするか
 */
export const darkenColor = (color: string, amount: number) => lightenColor(color, amount * -1);

/**
 * paletteのprimaryとsecondaryの色を変更する
 * @param primaryColor プライマリカラー
 * @param secondaryColor セカンダリカラー
 */
export const changePaletteColor = (primaryColor: string, secondaryColor: string) => {
  // ヘルプセンターでは基本的にdarkとmainしか使われないため、amountはdarkの値のみもとのテンプレになるべく合わせる値にしている

  const primary = {
    lighter: lightenColor(primaryColor, 100),
    light: lightenColor(primaryColor, 48),
    main: primaryColor,
    dark: darkenColor(primaryColor, 48),
    darker: darkenColor(primaryColor, 100),
  };

  const secondary = {
    lighter: lightenColor(secondaryColor, 100),
    light: lightenColor(secondaryColor, 48),
    main: secondaryColor,
    dark: darkenColor(secondaryColor, 48),
    darker: darkenColor(secondaryColor, 100),
  };

  return {
    primary,
    secondary,
  };
};

function getPaletteColor(siteConfig: SiteConfigurations) {

  // ヘルプセンターの機能拡張が有効な場合は、paletteの色を変更する
  return {
    primaryColor: (siteConfig?.color_main as string) ?? '#00AB55',
    secondaryColor: (siteConfig?.color_sub as string) ?? '#00B8D9',
    articleColor: (siteConfig?.color_article_label as string) ?? '#637381',
    textMainColor: (siteConfig?.color_main_text as string) ?? '#212B36',
    subTextColor: (siteConfig?.color_sub_text as string) ?? '#637381',
    buttonTextColor: (siteConfig?.color_button_text as string) ?? '#ffffff',
    pageBGColor: (siteConfig?.color_page_background as string) ?? '#ffffff',
    areaBGColor: (siteConfig?.color_area_background as string) ?? '#fbfcfc',
    borderColor: (siteConfig?.color_border as string) ?? '#e5e7eb',
    errorColor: (siteConfig?.color_error as string) ?? '#FF5630',
    symbolColor: (siteConfig?.color_icon_symbol as string) ?? '#637381',
  };

}

/**
 * カラーコードが有効な形式かどうかを確認する
 * @param color カラーコード
 * @returns 有効なカラーコードならtrue、そうでなければfalse
 */
const isValidColor = (color: string) => {
  // 正規表現でカラーコード形式を確認 (例: #nnnnnn)
  const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
  return hexColorRegex.test(color);
};

/**
 * ヘルプセンターで使うpaletteの色設定値を作成
 * @param siteConfig サイト設定
 */
export const changePaletteColorExtension = (siteConfig: SiteConfigurations) => {

  const primaryColor = (siteConfig?.color_main as string) ?? '#00AB55';
  const secondaryColor = (siteConfig?.color_sub as string) ?? '#00B8D9';
  const articleColor = (siteConfig?.color_article_label as string) ?? '#637381';
  const textMainColor = (siteConfig?.color_main_text && isValidColor(siteConfig.color_main_text)) ? siteConfig?.color_main_text : '#212B36';
  const subTextColor = (siteConfig?.color_sub_text as string) ?? '#637381';
  const buttonTextColor = (siteConfig?.color_button_text as string) ?? '#ffffff';
  const pageBGColor = (siteConfig?.color_page_background as string) ?? '#ffffff';
  const areaBGColor = (siteConfig?.color_area_background as string) ?? '#fbfcfc';
  const borderColor = (siteConfig?.color_border && isValidColor(siteConfig.color_border)) ? siteConfig?.color_border : '#e5e7eb';
  const errorColor = (siteConfig?.color_error && isValidColor(siteConfig.color_error)) ? siteConfig?.color_error : '#ff5630';
  const symbolColor = (siteConfig?.color_icon_symbol && isValidColor(siteConfig.color_icon_symbol)) ? siteConfig?.color_icon_symbol : '#637381';

  // ヘルプセンターでは基本的にdarkとmainしか使われないため、amountはdarkの値のみもとのテンプレになるべく合わせる値にしている
  const primary = {
    lighter: lightenColor(primaryColor, 100),
    light: lightenColor(primaryColor, 48),
    main: primaryColor,
    dark: darkenColor(primaryColor, 48),
    darker: darkenColor(primaryColor, 100),
  };

  const secondary = {
    lighter: lightenColor(secondaryColor, 100),
    light: lightenColor(secondaryColor, 48),
    main: secondaryColor,
    dark: darkenColor(secondaryColor, 48),
    darker: darkenColor(secondaryColor, 100),
  };

  const article = {
    lighter: lightenColor(articleColor, 100),
    light: lightenColor(articleColor, 48),
    main: articleColor,
    dark: darkenColor(articleColor, 48),
    darker: darkenColor(articleColor, 100),
  };

  const error = {
    lighter: alpha(errorColor, 0.20),
    light: alpha(errorColor, 0.40),
    main: errorColor,
    dark: darkenColor(errorColor, 74),
    darker: darkenColor(errorColor, 100),
  }

  const symbol = {
    lighter: alpha(symbolColor, 0.16),
    light: alpha(symbolColor, 0.48),
    main: symbolColor,
    dark: darkenColor(symbolColor, 74),
    darker: darkenColor(symbolColor, 100),
  }

  const extDriver = {
    lighter: alpha(borderColor, 0.20),
    light: alpha(borderColor, 0.40),
    main: borderColor,
    dark: darkenColor(borderColor, 74),
    darker: darkenColor(borderColor, 100),
  }

  const extTextMain = {
    lighter: alpha(textMainColor, 0.60),
    light: alpha(textMainColor, 0.80),
    main: textMainColor,
    dark: darkenColor(textMainColor, 74),
    darker: darkenColor(textMainColor, 100),
  }

  return {
    primary,
    secondary,
    article,
    textMainColor,
    subTextColor,
    buttonTextColor,
    pageBGColor,
    areaBGColor,
    borderColor,
    error,
    symbol,
    extDriver,
    extTextMain
  };
};
